/**
* Formatter class - parses & formats numbers and dates in a dazzling variety of languages
* @author Edward Petrenko
* @class Formatter
*/
/*globals document */

define([], function () {

	/**
	 *	@constructor
	 *	@param {string} lang - a two letter string denoting the desired language (ie: "EN" or "fr"), defaults to English.
	 */
    function Formatter(lang) {
		lang = lang || document.documentElement.lang;
		this.options = {};
		//this.options.numberOfDecimals = 0;
		this.setLanguage(lang);
    }

	/** Sets the language of the formatter.
	 *	@param {string} lang - a two letter string denoting the desired language (ie: "EN" or "fr"), defaults to English.
	 */
	Formatter.prototype.setLanguage = function(lang) {
		this.lang = (typeof(lang) === "string") ? lang.toUpperCase() : lang;
		switch (this.lang){
			case "FR":
				this.options.startSymbol = "";
				this.options.endSymbol = "\u00A0$";
				this.options.decimalSeparator = ",";
				this.options.separator = "\u00A0";
				this.options.dateSeparator = "/";
				this.options.dateFormat = "DD/MM/YYYY";
				this.options.percentage = "\u00A0%";
				break;
			case "EN-US":
            case "EN": // jshint ignore:line
			default:
				this.options.startSymbol = '$';
				this.options.endSymbol = '';
				this.options.decimalSeparator = '.';
				this.options.separator = ',';
				this.options.dateSeparator = "/";
				this.options.dateFormat = "MM/DD/YYYY";
				this.options.percentage = "%";
				break;
		}
	};

    /** Parses a number from a string. Strips everything from the string except numbers, the first negative sign, and the first decimal sign (in that particular language).
	 *	@param {string} - number
	 *	@returns {number}
	 */
    Formatter.prototype.parseNumber = function (n) {
		var str,
			negative = "",
			decimalIndex;

		if (n === null || typeof n === "undefined") { return null; }
		if (typeof n === "number") { return n; }

		str = n.toString();

		//strips everything except numbers, negative signs and the decimal separator
		str = str.replace(new RegExp('[^0-9\-\\' + this.options.decimalSeparator + ']', 'g'), '');

		//strips negative signs, but remembers if the number was negative
		if (str[0] === "-") {negative = "-";}
		str = str.replace(/\-/g, '');
        if (str.length === 0) { return null; }

		//takes the first decimal place as the intended one, removes all others, converts it to a proper decimal place
		decimalIndex = str.indexOf(this.options.decimalSeparator);
		str = str.replace(new RegExp("\\" + this.options.decimalSeparator, "g"), "");
		if (decimalIndex >= 0) {
			str = str.substr(0, decimalIndex) + '.' + str.substr(decimalIndex);
		}

		//reapplies the negative sign (if it was present at the start)
		str = negative + str;

		if (isNaN(parseFloat(str))) {
			return null;
		} else {
			return parseFloat(str);
		}
	};


	/** Formats a number into a more presentable string.
	 *	@param {number} n - the number to format (though, if a string is provided, it will have parseNumber called on it)
	 *	@param {number} numDecimals - (optional) the number of decimals to display to. If not provided, defaults the number of decimals in the number (n)
	 *	@param {string} thousandsSeparator - (optional) the string to separate groups of thousands. If not provided, defaults to this.options.separator;
	 *	@returns {string}
	 */
    Formatter.prototype.formatNumber = function (n, numDecimals, thousandsSeparator) {
		if (typeof n === "string") {
			n = this.parseNumber(n);
		}
		if (isNaN(n)) {
			throw new Error("Formatter.formatNumber - illegal argument: expected a number, received: " + typeof n + ": " + n);
		}

		var formatted = "",
			decimalIndex,
			nonDecimalNumbers,
			nonDecimalLength,
			str,
			decimals,
			thousandsChar = thousandsSeparator || this.options.separator,
			x;

		if (typeof numDecimals === "undefined" && n.toString().indexOf('.') >= 0) { decimals = n.toString().length - n.toString().indexOf('.') - 1; }
		if (typeof numDecimals !== "undefined") {
			n = this.round(n, numDecimals);
			decimals = numDecimals;
		}

        str = n.toString();

		//applies negative sign, if necessary
		if (n < 0) {
			formatted += "-";
			str = str.slice(1);
		}

		//finds the decimal spot and isolates all numbers left of it
		decimalIndex = str.indexOf('.');
		nonDecimalNumbers = (decimalIndex >= 0) ? str.slice(0, decimalIndex) : str.slice(0);
		nonDecimalLength = nonDecimalNumbers.length;

		//if the number requires thousands separators, adds them into the string
		if (nonDecimalLength >= 4) {
			for (x = nonDecimalLength; x > 0; x--) {
				if ((x !== 0) && (x - nonDecimalLength !== 0) && (x % 3 === 0)) {
					formatted += thousandsChar;
				}
				formatted += nonDecimalNumbers[nonDecimalLength - x];
			}
		} else {
			formatted += nonDecimalNumbers.slice(0);
		}

		//builds out decimals to the required accuracy
		if (decimals) {
			formatted += this.options.decimalSeparator;
			decimalIndex = (decimalIndex < 0) ? nonDecimalLength : decimalIndex;
			for (x = decimalIndex + 1; x <= decimalIndex + decimals; x++) {
				formatted += str[x] || "0";
			}
		}

		return formatted;
	};

	Formatter.prototype.round = function(num, places, roundingError) {
		roundingError = roundingError || 0;
		var error = Math.pow(10, -places) * roundingError;
		return (num + error).toFixed(places);
	};


	/** Formats a number into a currency string. Just like formatNumber, but with dollar signs.
	 *	@param {number} n - the number to format (though, if a string is provided, it will have parseNumber called on it)
	 *	@param {number} numDecimals - (optional) the number of decimals to display to. If not provided, defaults the number of decimals in the number (n)
	 *	@param {string} thousandsSeparator - (optional) the string to separate groups of thousands. If not provided, defaults to this.options.separator;
	 *	@returns {string}
	 */
    Formatter.prototype.formatCurrency = function (v, numDecimals, thousandsSeparator) {
		if (typeof v === "string") {
			v = this.parseNumber(v);
		}
		if (isNaN(v)) {
			throw new Error("Formatter.formatCurrency - illegal argument: expected a number, received: " + typeof v + ": " + v);
		}
		var formatted = "";

		if (v < 0) {
			formatted = "-";
			v = Math.abs(v);
		}

		formatted += this.options.startSymbol;
		formatted += this.formatNumber(v, numDecimals, thousandsSeparator);
		if (this.options.endSymbol !== "") {
			formatted += this.options.endSymbol;
		}
		return formatted;
	};


	/** Formats a number into a currency string. Just like formatNumber, but with dollar signs.
	 *	@param {number} n - the number to format (though, if a string is provided, it will have parseNumber called on it)
	 *	@param {number} numDecimals - (optional) the number of decimals to display to. If not provided, defaults the number of decimals in the number (n)
	 *	@param {string} thousandsSeparator - (optional) the string to separate groups of thousands. If not provided, defaults to this.options.separator;
	 *	@returns {string}
	 */
    Formatter.prototype.formatPercentage = function (v, numDecimals, thousandsSeparator) {
		if (typeof v === "string") {
			v = this.parseNumber(v);
		}
		if (isNaN(v)) {
			throw new Error("Formatter.formatPercentage - illegal argument: expected a number, received: " + typeof v + ": " + v);
		}
		var formatted = "";

		formatted += this.formatNumber(v, numDecimals, thousandsSeparator);
		formatted += this.options.percentage;
		return formatted;
	};

	Formatter.prototype.formatFinancial = function(v, numDecimals, thousandsSeparator) {
		var formatted;
		if (typeof v === "string") {v = this.parseNumber(v);}
		if (isNaN(v)) {throw new Error("Formatter.formatFinancial - illegal argument: expected a number, received: " + v);}

		formatted = this.formatCurrency(v, numDecimals, thousandsSeparator);
		return (formatted.charAt(0) === "-") ? "(" + formatted.substr(1) + ")" : formatted;
	};





	/** Takes a simpleDate object (or a month and, optionally, a year) and returns a nicely formatted, verbose date sentence. If no parameters are provided, formats today's date.
	 *	@param {string|simpleDate} d - a simpleDate object, or a date string ("02/28/1977")
	 *	@param {boolean|object} options - an options object
	 *                                  - OLD: This used to be abbrev. Still supported as boolean.
	 *  @param {boolean} [options.abbrev=false] - whether or not to abbreviate the month name (if true, will output "Jan." instead of "January", for example.
	 *  @param {boolean} [options.suffix=true] - whether of not to include the suffix on the day.
	 *	@returns {string} A formatted date
	 */
	Formatter.prototype.formatDate = function (d, format) {
		var str,
			dateObject = d || new Date(),
			year = dateObject.getFullYear(),
			month = dateObject.getMonth(),
			date = dateObject.getDate(),
			formatParts = {
				yy: 	year.toString().slice(2),
				yyyy: 	year.toString(),
				m:		(month + 1).toString(),
				mm:		this.padWithZeroes(month + 1, 2),
				mmm:	this.getMonthAbbreviation(month),
				mmmm:	this.getMonth(month),
				d:		date.toString(),
				dd:		this.padWithZeroes(date, 2),
				ddd:	date.toString() + this.getDateSuffix(date),
				dddd:	this.padWithZeroes(date, 2) + this.getDateSuffix(date)
			},
			orderToSearch = Object.keys(formatParts).sort(function(a, b){return b.length - a.length;}),
			lettersAlreadySearched = "";

		format = format || this.options.dateFormat;
		str = format.toLowerCase();

		for (var x = 0; x < orderToSearch.length; x++) {
			if (str.indexOf(orderToSearch[x]) >= 0 && lettersAlreadySearched.indexOf(orderToSearch[x].charAt(0)) < 0) {
				str = str.replace(orderToSearch[x], formatParts[orderToSearch[x]]);
				lettersAlreadySearched += orderToSearch[x];
			}
		}

		return str;
	};




	/** Takes a number (1-12) and returns the name of the month. Also accepts simplified date objects. If no parameters are provided, gets the current month.
	 *	@param {number|simpleDate} m - the month, either a number or a simpleDate object
	 *	@returns {string} The name of the month.
	 */
	Formatter.prototype.getMonth = function(date) {
		if (typeof date === "object") { date = date.getMonth(); }
		if (isNaN(date)) { throw new Error("Formatter.getMonth - invalid date: " + date); }
		if (date < 0 || date > 11) { throw new Error("Formatter.getMonth - invalid date: " + date); }
		var months;
		switch (this.lang) {
			case 'FR':
				months = [
					"janvier",
					"f\u00E9vrier",
					"mars",
					"avril",
					"mai",
					"juin",
					"juillet",
					"ao\u00FBt",
					"septembre",
					"octobre",
					"novembre",
					"d\u00E9cembre"
					];
				break;
            case "EN": //jshint ignore:line
			default:
				months = [
					"January",
					"February",
					"March",
					"April",
					"May",
					"June",
					"July",
					"August",
					"September",
					"October",
					"November",
					"December"
					];
				break;
		}
		return months[date];
	};

	/** Takes a number (1-12) and returns the abbreviated name of the month. Also accepts simplified date objects. If no parameters are provided, gets the current month.
	 *	@param {number|simpleDate} m - the month, either a number or a simpleDate object
	 *	@returns {string} The name of the month, abbreviated (ie: "January" becomes "Jan.").
	 */
	Formatter.prototype.getMonthAbbreviation = function(date) {
		if (typeof date === "object") { date = date.getMonth(); }
		if (isNaN(date)) { throw new Error("Formatter.getMonthAbbreviation - invalid date: " + date); }
		if (date < 0 || date > 11) { throw new Error("Formatter.getMonthAbbreviation - invalid date: " + date); }
		var months;
		switch (this.lang) {
			case "FR":
				months = [
					"janv",
					"f\u00E9vr",
					"mars",
					"avril",
					"mai",
					"juin",
					"juil",
					"ao\u00FBt",
					"sept",
					"oct",
					"nov",
					"d\u00E9c"
					];
				break;
			case "EN": //jshint ignore:line
			default:
				months = [
					"Jan",
					"Feb",
					"Mar",
					"Apr",
					"May",
					"June",
					"July",
					"Aug",
					"Sept",
					"Oct",
					"Nov",
					"Dec"
					];
				break;
		}
		return months[date];
	};



	/** Takes a simpleDate object (or a month and, optionally, a year) and returns the number of days in that month. Can account for February in leap years. If no parameters provided, gets current month's days.
	 *	@param {number|simpleDate} m - the month, either a number (1-12) or a simpleDate object
	 *	@param {number} y - (optional) the year
	 *	@returns {number} The number of days in that month.
	 */
	Formatter.prototype.getMonthDays = function(date) {
		if (typeof date === "undefined") {
			date = new Date();
		}
		switch (date.getMonth()) {
			case 0: return 31;
			case 1:
				if ((date.getFullYear() % 400 === 0) || (date.getFullYear() % 4 === 0 && date.getFullYear() % 100 !== 0)) {
					return 29;
				}
				return 28;
			case 2: return 31;
			case 3: return 30;
			case 4: return 31;
			case 5: return 30;
			case 6: return 31;
			case 7: return 31;
			case 8: return 30;
			case 9: return 31;
			case 10: return 30;
			case 11: return 31;
			default: throw new Error("Impossible date received: " + date);
		}
	};


	/** Takes a simpleDate object (or a number) and returns the ordinal suffix of that number, as it would be used in a date in the specified language.
	 *	@param {number|simpleDate} n - the number of a day, or a simpleDate object
	 *	@returns {string} The ordinal suffix: put in 1, get "st"; put in 2, get "nd"; put in 3, get "rd", etc
	 */
	Formatter.prototype.getDateSuffix = function(date) {
		var lastDigit;
		if (typeof date === "object") {date = date.getDate();}
		if (isNaN(date)) {return "";}
		switch(this.lang) {
			case "FR":
				switch (date) {
					case 1:
						return "er";
					default:
						return "";
				}
				break;
			case "EN": //jshint ignore:line
			default:
				lastDigit = date.toString();
				switch (lastDigit.charAt(lastDigit.length - 1)) {
					case "1":
						return (lastDigit.charAt(lastDigit.length - 2) == '1') ? 'th' : 'st';
					case "2":
						return (lastDigit.charAt(lastDigit.length - 2) == '1') ? 'th' : 'nd';
					case "3":
						return (lastDigit.charAt(lastDigit.length - 2) == '1') ? 'th' : 'rd';
					default:
						return "th";
				}
				break;
		}
	};


	Formatter.prototype.getAge = function(date) {
		var birthDate = date, currentDate, age;
		if (typeof birthDate === "number") {
			birthDate = new Date(new Date().setFullYear(date));	//if only a number is provided, assume it's a year, and assume a January 1st birthday
		}
		currentDate = new Date();
		age = currentDate.getFullYear() - birthDate.getFullYear() - 1;
		age += (birthDate.getMonth() < currentDate.getMonth()) ? 1 : 0;
		age += (birthDate.getMonth() === currentDate.getMonth() && birthDate.getDate() <= currentDate.getDate()) ? 1 : 0;
		return age;
	};

	
	//Formatter.prototype.toProperCase = function (str) {
	//	return str.replace(/\w\S*/g, function(txt){return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();});
	//};
	
	Formatter.prototype.padWithZeroes = function(number, digits) {
		var str = number.toString(), zeroes = [];
		digits = digits || 2;
		for (var x = str.length; x < digits; x++) {
			zeroes.push("0");
		}
		zeroes = zeroes.toString().replace(",", "");
		return zeroes + str;
	};
	
	

	Formatter.prototype.compareDates = function(a, b) {
		if (a.getFullYear() > b.getFullYear()) { return -1; }
		if (a.getFullYear() === b.getFullYear()) {
			if (a.getMonth() > b.getMonth()) { return -1; }
			if (a.getMonth() === b.getMonth()) {
				if (a.getDate() > b.getDate()) { return -1; }
				if (a.getDate() === b.getDate()) { return 0; }
			}
		}
		return 1;
	};

    return Formatter;
});
